import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import { observer } from "mobx-react-lite";

import { cn } from "@/lib/utils";
import { usePlayground } from "@/components/Playground/playground-store";
import { IMessageStore } from "@/components/Playground/Messages/Message/message-store";
import { PlaceholderContainerContent } from "@/components/Playground/Messages/PlaceholderContainer/Content";
import { PlaceholderContainerFooter } from "@/components/Playground/Messages/PlaceholderContainer/Footer";
import { PlaceholderContainerHeader } from "@/components/Playground/Messages/PlaceholderContainer/Header";
import { useBiSyncPlaceholder } from "@/components/Playground/Messages/PlaceholderContainer/hooks/useBiSyncPlaceholder";
import { usePlaceholderContainerStore } from "@/components/Playground/Messages/PlaceholderContainer/hooks/usePlaceholderContainerStore";
import { PlaceholderContainerProvider } from "@/components/Playground/Messages/PlaceholderContainer/context/placeholder-container-context";

interface PlaceholderContainerProps {
  message: IMessageStore;
  index: number;
  readonly?: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps;
}

const Content = observer(() => {
  // Sync child messages to parent message store (bidirectional)
  useBiSyncPlaceholder();

  return (
    <div
      className={cn(
        "group/placeholder relative flex w-full flex-col gap-y-1 rounded-lg px-3 py-3 transition-all duration-150 hover:bg-gray-50",
      )}
    >
      <PlaceholderContainerHeader />
      <PlaceholderContainerContent />
      <PlaceholderContainerFooter />
    </div>
  );
});

export const PlaceholderContainer = observer(
  ({ message, index, readonly: globalReadonly, dragHandleProps }: PlaceholderContainerProps) => {
    const playground = usePlayground();
    const variableSets = playground.input_variable_sets;
    const { placeholderStore, childMessageStore, childMessageStoresMap } =
      usePlaceholderContainerStore();

    const readonly =
      globalReadonly ||
      playground.isXRayMode ||
      (playground.mode === "completion" && playground.operationStore.isRunning);

    const leftBraces = playground.template_format === "jinja2" ? "{{" : "{";
    const rightBraces = playground.template_format === "jinja2" ? "}}" : "}";

    const contextValue = {
      message,
      index,
      readonly,
      dragHandleProps,
      placeholderStore,
      childStore: childMessageStore,
      childStoresMap: childMessageStoresMap,
      variableSets,
      leftBraces,
      rightBraces,
    };

    return (
      <PlaceholderContainerProvider value={contextValue}>
        <Content />
      </PlaceholderContainerProvider>
    );
  },
);
